import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Grid,
  Alert,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  FormControlLabel,
  Switch,
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import AudioUploader from './AudioUploader';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { useApp } from '../../contexts/AppContext';
import meetingService from '../../services/meetingService';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import SecurityIcon from '@mui/icons-material/Security';
import InfoIcon from '@mui/icons-material/Info';
import BaseDialog from './BaseDialog';
import BaseTextField from './BaseTextField';
import BaseButton from './BaseButton';
import ProjectSelector from './ProjectSelector';

const EditMeetingDialog = ({ open, onClose, onSuccess, meetingId }) => {
  const { currentUser } = useAuth();
  const { addSystemNotification } = useNotifications();
  const { setError } = useApp();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [loading, setLoading] = useState(false);
  const [loadingMeeting, setLoadingMeeting] = useState(false);
  const [meetingData, setMeetingData] = useState({
    title: '',
    date: new Date().toISOString().slice(0, 10),
    context: '',
    participants: [],
    audioUrl: '',
    fileName: '',
    fileSize: null,
    fileType: '',
    isConfidential: false,
    projectId: null
  });
  const [originalMeeting, setOriginalMeeting] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [newParticipant, setNewParticipant] = useState({ displayName: '', title: '' });
  const [audioFile, setAudioFile] = useState(null);

  // Charger les données de la réunion à l'ouverture
  useEffect(() => {
    const fetchMeetingData = async () => {
      if (!open || !meetingId) return;
      
      setLoadingMeeting(true);
      try {
        const meeting = await meetingService.getMeetingById(meetingId);
        
        if (!meeting) {
          setError('Réunion non trouvée');
          onClose();
          return;
        }
        
        setOriginalMeeting(meeting);
        
        // Convertir les participants si nécessaire
        let participantsArray = [];
        if (meeting.participants) {
          if (typeof meeting.participants === 'object' && Array.isArray(meeting.participants)) {
            participantsArray = meeting.participants;
          } else if (typeof meeting.participants === 'string') {
            participantsArray = meeting.participants.split(',')
              .map(name => name.trim())
              .filter(name => name)
              .map(name => ({ displayName: name, title: '' }));
          }
        }
        
        setMeetingData({
          title: meeting.title || '',
          date: meeting.date || new Date().toISOString().slice(0, 10),
          context: meeting.context || '',
          participants: participantsArray,
          audioUrl: meeting.audioUrl || '',
          fileName: meeting.fileName || '',
          fileSize: meeting.fileSize || null,
          fileType: meeting.fileType || '',
          isConfidential: meeting.isConfidential || false,
          projectId: meeting.projectId || null
        });
      } catch (error) {
        addSystemNotification('Erreur lors du chargement de la réunion:', 'error');
        setError('Erreur lors du chargement de la réunion');
        onClose();
      } finally {
        setLoadingMeeting(false);
      }
    };
    
    fetchMeetingData();
  }, [open, meetingId, onClose, setError]);

  // Reset du formulaire à la fermeture
  const handleClose = () => {
    setMeetingData({
      title: '',
      date: new Date().toISOString().slice(0, 10),
      context: '',
      participants: [],
      audioUrl: '',
      fileName: '',
      fileSize: null,
      fileType: '',
      isConfidential: false,
      projectId: null
    });
    setValidationErrors({});
    setNewParticipant({ displayName: '', title: '' });
    setAudioFile(null);
    setOriginalMeeting(null);
    onClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setMeetingData({
      ...meetingData,
      [name]: value
    });
    
    if (validationErrors[name]) {
      setValidationErrors({
        ...validationErrors,
        [name]: ''
      });
    }
  };

  const handleAddParticipant = () => {
    if (!newParticipant.displayName.trim()) return;
    setMeetingData({
      ...meetingData,
      participants: [...meetingData.participants, { ...newParticipant }]
    });
    setNewParticipant({ displayName: '', title: '' });
  };

  const handleRemoveParticipant = (index) => {
    const updatedParticipants = [...meetingData.participants];
    updatedParticipants.splice(index, 1);
    setMeetingData({
      ...meetingData,
      participants: updatedParticipants
    });
  };

  const handleFileSelect = (file) => {
    setAudioFile(file);
  };

  const validateForm = () => {
    const errors = {};
    
    if (!meetingData.title.trim()) {
      errors.title = 'Le titre est requis';
    }
    
    if (!meetingData.date) {
      errors.date = 'La date est requise';
    }
    
    if (!Array.isArray(meetingData.participants) || meetingData.participants.length === 0) {
      errors.participants = 'Veuillez ajouter au moins un participant.';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Préparer les données à mettre à jour
      const updatedData = {
        ...originalMeeting,
        title: meetingData.title,
        date: meetingData.date,
        context: meetingData.context,
        participants: meetingData.participants,
        isConfidential: meetingData.isConfidential,
        projectId: meetingData.projectId
      };

      // Si un nouveau fichier audio a été sélectionné
      if (audioFile) {
        // TODO: Implémenter l'upload du nouveau fichier
        // Pour l'instant, on garde l'ancien fichier
        addSystemNotification('Upload de nouveau fichier audio pas encore implémenté', 'error');
      }

      // Mettre à jour la réunion
      await meetingService.updateMeeting(meetingId, updatedData);

      if (onSuccess) {
        onSuccess(meetingId);
      }
      handleClose();
    } catch (error) {
      addSystemNotification('Erreur lors de la mise à jour de la réunion:', 'error');
      setError('Erreur lors de la mise à jour de la réunion');
    } finally {
      setLoading(false);
    }
  };

  // Actions du dialog
  const dialogActions = (
    <>
      <BaseButton
        variant="outlined"
        onClick={handleClose}
        disabled={loading || loadingMeeting}
      >
        Annuler
      </BaseButton>
      <BaseButton
        gradient
        onClick={handleSubmit}
        disabled={loading || loadingMeeting}
        sx={{ minWidth: '140px' }}
      >
        {loading ? <CircularProgress size={20} sx={{ color: 'white', mr: 1 }} /> : <SaveIcon sx={{ mr: 1 }} />}
        {loading ? 'Enregistrement...' : 'Enregistrer'}
      </BaseButton>
    </>
  );

  return (
    <BaseDialog
      open={open}
      onClose={handleClose}
      title="✏️ Modifier la Réunion"
      subtitle="Modifiez les informations de votre réunion"
      maxWidth="md"
      actions={dialogActions}
      fullScreen={false}
    >
      {loadingMeeting ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <form onSubmit={handleSubmit}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <BaseTextField
                name="title"
                label="📝 Titre de la réunion"
                fullWidth
                value={meetingData.title}
                onChange={handleChange}
                error={!!validationErrors.title}
                helperText={validationErrors.title}
                required
                size="medium"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                    fontWeight: 400,
                    fontSize: '14px',
                    backgroundColor: '#ffffff',
                    '& fieldset': {
                      borderColor: '#cbd5e1'
                    },
                    '&:hover fieldset': {
                      borderColor: '#94a3b8'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#3b82f6',
                      borderWidth: '2px'
                    }
                  },
                  '& .MuiInputLabel-root': {
                    fontWeight: 500,
                    fontSize: '14px',
                    color: '#475569',
                    fontFamily: '"Inter", sans-serif',
                    '&.Mui-focused': { color: '#3b82f6' }
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <BaseTextField
                name="date"
                label="📅 Date de la réunion"
                type="date"
                fullWidth
                value={meetingData.date}
                onChange={handleChange}
                error={!!validationErrors.date}
                helperText={validationErrors.date}
                required
                size="medium"
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                    fontWeight: 400,
                    fontSize: '14px',
                    backgroundColor: '#ffffff',
                    '& fieldset': {
                      borderColor: '#cbd5e1'
                    },
                    '&:hover fieldset': {
                      borderColor: '#94a3b8'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#3b82f6',
                      borderWidth: '2px'
                    }
                  },
                  '& .MuiInputLabel-root': {
                    fontWeight: 500,
                    fontSize: '14px',
                    color: '#475569',
                    fontFamily: '"Inter", sans-serif',
                    '&.Mui-focused': { color: '#3b82f6' }
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography 
                variant="subtitle2" 
                gutterBottom 
                sx={{ 
                  mt: 1, 
                  mb: 2.5,
                  fontWeight: 600,
                  color: '#1e293b',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  fontFamily: '"Inter", sans-serif',
                  fontSize: '15px'
                }}
              >
                👥 Participants (obligatoire)
              </Typography>
              <Stack direction={isMobile ? "column" : "row"} spacing={2} sx={{ mb: 2.5 }}>
                <BaseTextField
                  label="Nom"
                  value={newParticipant.displayName}
                  onChange={e => setNewParticipant(prev => ({ ...prev, displayName: e.target.value }))}
                  size="medium"
                  sx={{ 
                    flex: 2,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      fontFamily: '"Inter", sans-serif',
                      fontWeight: 400,
                      fontSize: '14px',
                      backgroundColor: '#ffffff',
                      '& fieldset': { borderColor: '#cbd5e1' },
                      '&:hover fieldset': { borderColor: '#94a3b8' },
                      '&.Mui-focused fieldset': { borderColor: '#3b82f6', borderWidth: '2px' }
                    },
                    '& .MuiInputLabel-root': {
                      fontWeight: 500,
                      fontSize: '14px',
                      color: '#475569',
                      fontFamily: '"Inter", sans-serif'
                    }
                  }}
                />
                <BaseTextField
                  label="Poste"
                  value={newParticipant.title}
                  onChange={e => setNewParticipant(prev => ({ ...prev, title: e.target.value }))}
                  size="medium"
                  sx={{ 
                    flex: 2,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      fontFamily: '"Inter", sans-serif',
                      fontWeight: 400,
                      fontSize: '14px',
                      backgroundColor: '#ffffff',
                      '& fieldset': { borderColor: '#cbd5e1' },
                      '&:hover fieldset': { borderColor: '#94a3b8' },
                      '&.Mui-focused fieldset': { borderColor: '#3b82f6', borderWidth: '2px' }
                    },
                    '& .MuiInputLabel-root': {
                      fontWeight: 500,
                      fontSize: '14px',
                      color: '#475569',
                      fontFamily: '"Inter", sans-serif'
                    }
                  }}
                />
                <BaseButton 
                  variant="contained" 
                  size="medium"
                  onClick={handleAddParticipant}
                  disabled={!newParticipant.displayName.trim()}
                  sx={{ 
                    px: 3, 
                    py: 1.5,
                    minWidth: 'auto',
                    alignSelf: isMobile ? 'flex-start' : 'stretch',
                    borderRadius: '6px',
                    fontFamily: '"Inter", sans-serif',
                    fontWeight: 500,
                    textTransform: 'none'
                  }}
                >
                  <AddIcon fontSize="small" />
                </BaseButton>
              </Stack>
              
              {meetingData.participants.length > 0 && (
                <Box sx={{ 
                  maxHeight: 140, 
                  overflow: 'auto', 
                  background: '#ffffff',
                  border: '1px solid #e2e8f0', 
                  borderRadius: '8px',
                  '&::-webkit-scrollbar': {
                    width: '6px'
                  },
                  '&::-webkit-scrollbar-track': {
                    background: '#f1f5f9'
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#cbd5e1',
                    borderRadius: '3px'
                  }
                }}>
                  {meetingData.participants.map((p, idx) => (
                    <Box 
                      key={idx} 
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        p: 2.5, 
                        borderBottom: idx < meetingData.participants.length - 1 ? '1px solid #f1f5f9' : 'none',
                        '&:hover': {
                          background: '#f8fafc'
                        },
                        transition: 'background 0.2s ease'
                      }}
                    >
                      <Box>
                        <Typography 
                          variant="body2" 
                          fontWeight={500} 
                          sx={{ 
                            color: '#1e293b', 
                            mb: 0.5,
                            fontFamily: '"Inter", sans-serif',
                            fontSize: '14px'
                          }}
                        >
                          {p.displayName}
                        </Typography>
                        {p.title && (
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: '#64748b', 
                              fontFamily: '"Inter", sans-serif',
                              fontSize: '13px'
                            }}
                          >
                            {p.title}
                          </Typography>
                        )}
                      </Box>
                      <IconButton 
                        size="small" 
                        onClick={() => handleRemoveParticipant(idx)}
                        sx={{
                          color: '#dc2626',
                          background: '#fef2f2',
                          borderRadius: '6px',
                          width: 32,
                          height: 32,
                          '&:hover': {
                            background: '#fee2e2',
                            transform: 'scale(1.05)'
                          },
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
              
              {validationErrors.participants && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {validationErrors.participants}
                </Alert>
              )}
            </Grid>
            
            <Grid item xs={12}>
              <BaseTextField
                name="context"
                label="💭 Contexte de la réunion"
                fullWidth
                multiline
                rows={3}
                value={meetingData.context}
                onChange={handleChange}
                placeholder="Décrivez brièvement le contexte, les objectifs ou les sujets abordés..."
                size="medium"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                    fontWeight: 400,
                    fontSize: '14px',
                    backgroundColor: '#ffffff',
                    '& fieldset': {
                      borderColor: '#cbd5e1'
                    },
                    '&:hover fieldset': {
                      borderColor: '#94a3b8'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#3b82f6',
                      borderWidth: '2px'
                    }
                  },
                  '& .MuiInputLabel-root': {
                    fontWeight: 500,
                    fontSize: '14px',
                    color: '#475569',
                    fontFamily: '"Inter", sans-serif',
                    '&.Mui-focused': { color: '#3b82f6' }
                  },
                  '& .MuiOutlinedInput-input::placeholder': {
                    color: '#94a3b8',
                    opacity: 1,
                    fontFamily: '"Inter", sans-serif'
                  }
                }}
              />
            </Grid>

            {/* Sélecteur de projet */}
            <Grid item xs={12}>
              <ProjectSelector
                value={meetingData.projectId ? { id: meetingData.projectId } : null}
                onChange={(project) => setMeetingData(prev => ({
                  ...prev,
                  projectId: project?.id || null
                }))}
                disabled={loading}
                currentMeetingId={meetingId}
              />
            </Grid>

            {/* Option Confidentialité */}
            <Grid item xs={12}>
              <Box 
                sx={{ 
                  background: '#ffffff',
                  borderRadius: '8px',
                  p: 3,
                  border: meetingData.isConfidential 
                    ? '2px solid #8b5cf6' 
                    : '1px solid #e2e8f0',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    borderColor: meetingData.isConfidential ? '#7c3aed' : '#cbd5e1',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flex: 1 }}>
                    <SecurityIcon 
                      sx={{ 
                        color: meetingData.isConfidential ? '#8b5cf6' : '#64748b',
                        fontSize: '20px',
                        transition: 'color 0.2s ease',
                        mt: 0.25
                      }} 
                    />
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography 
                          variant="subtitle2" 
                          sx={{ 
                            fontWeight: 600,
                            color: meetingData.isConfidential ? '#8b5cf6' : '#1e293b',
                            transition: 'color 0.2s ease',
                            fontFamily: '"Inter", sans-serif',
                            fontSize: '15px',
                            lineHeight: 1.3
                          }}
                        >
                          Réunion confidentielle
                        </Typography>
                        <Tooltip
                          title={
                            <Box sx={{ p: 1 }}>
                              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                                🔒 Mode Confidentiel
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.4 }}>
                                • Seul vous aurez accès à cette réunion
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.4 }}>
                                • Les administrateurs ne pourront pas la consulter
                              </Typography>
                              <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.4 }}>
                                • L'accès technique en base reste possible pour la maintenance
                              </Typography>
                            </Box>
                          }
                          arrow
                          placement="top"
                        >
                          <IconButton 
                            size="small"
                            sx={{ 
                              p: 0.25,
                              color: '#64748b',
                              '&:hover': {
                                background: '#f1f5f9',
                                color: '#3b82f6'
                              }
                            }}
                          >
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#64748b',
                          fontFamily: '"Inter", sans-serif',
                          fontSize: '13px',
                          lineHeight: 1.4
                        }}
                      >
                        {meetingData.isConfidential 
                          ? "🔐 Accès exclusif - Visible uniquement par vous"
                          : "👥 Accès standard - Visible par vous et les administrateurs"
                        }
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Switch
                    checked={meetingData.isConfidential}
                    onChange={(e) => setMeetingData(prev => ({ 
                      ...prev, 
                      isConfidential: e.target.checked 
                    }))}
                    sx={{
                      width: 52,
                      height: 32,
                      padding: 0,
                      margin: 0,
                      '& .MuiSwitch-switchBase': {
                        padding: '0px',
                        margin: 0,
                        '&.Mui-checked': {
                          color: '#ffffff',
                          transform: 'translateX(20px)',
                          '& + .MuiSwitch-track': {
                            backgroundColor: '#8b5cf6',
                            opacity: 1,
                            border: 'none'
                          }
                        }
                      },
                      '& .MuiSwitch-thumb': {
                        width: 20,
                        height: 20,
                        backgroundColor: '#ffffff',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                      },
                      '& .MuiSwitch-track': {
                        borderRadius: 16,
                        backgroundColor: '#cbd5e1',
                        opacity: 1,
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        border: '1px solid #e2e8f0',
                        width: 40,
                        height: 20
                      }
                    }}
                  />
                </Box>
              </Box>
            </Grid>

            {/* Informations sur le fichier audio actuel */}
            {meetingData.audioUrl && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                  🎵 Fichier audio actuel
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>{meetingData.fileName}</strong>
                    {meetingData.fileSize && (
                      <span> ({(meetingData.fileSize / (1024 * 1024)).toFixed(2)} MB)</span>
                    )}
                  </Typography>
                </Alert>
                
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Pour remplacer le fichier audio, sélectionnez un nouveau fichier ci-dessous :
                </Typography>
                <AudioUploader onFileSelect={handleFileSelect} />
                
                {audioFile && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      <strong>Attention :</strong> Remplacer le fichier audio supprimera la transcription existante. 
                      Une nouvelle transcription devra être généréée.
                    </Typography>
                  </Alert>
                )}
              </Grid>
            )}
          </Grid>
        </form>
      )}
    </BaseDialog>
  );
};

export default EditMeetingDialog; 